import React, { Component, ReactNode, ErrorInfo } from 'react';

/**
 * Error Boundary component to catch errors in UI component rendering
 */
export class ComponentErrorBoundary extends Component<
  { componentName: string; fallback?: ReactNode; children: ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: {
    componentName: string;
    fallback?: ReactNode;
    children: ReactNode;
  }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log the error to an error reporting service
    console.error(
      `Error rendering component ${this.props.componentName}:`,
      error,
      errorInfo,
    );
  }

  render() {
    if (this.state.hasError) {
      // You can render any custom fallback UI
      return (
        this.props.fallback || (
          <div className='error-boundary p-2 border border-danger rounded'>
            <p className='text-danger mb-1'>
              Failed to render component: {this.props.componentName}
            </p>
            <small className='text-muted'>
              {this.state.error?.message || 'Unknown error'}
            </small>
          </div>
        )
      );
    }

    return this.props.children;
  }
}

export default ComponentErrorBoundary;
